import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "time-picker-input" }
const _hoisted_2 = ["id", "name", "value", "disabled", "placeholder"]
const _hoisted_3 = { class: "d-flex align-items-center justify-content-center gap-2" }
const _hoisted_4 = { class: "time-section text-center" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "time-display my-2" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "time-section text-center" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "time-display my-2" }
const _hoisted_11 = ["disabled"]

import { ref, computed, watch } from "vue";
import { useFloating, autoUpdate, offset, flip, shift } from "@floating-ui/vue";

import { onMounted, onUnmounted } from "vue";


export default {
  __name: 'TimePickerPopover',
  props: {
  modelValue: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  inputId: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Zeit wählen",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  inputClass: {
    type: [String, Object, Array],
    default: "",
  },
},
  emits: ["update:modelValue", "blur", "change"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Popover state
const popoverOpen = ref(false);
const popoverEl = ref(null);
const inputRef = ref(null);

// Internal time state
const internalHours = ref(0);
const internalMinutes = ref(0);

// Setup floating UI for popover
const { floatingStyles: popoverStyles } = useFloating(
  inputRef,
  popoverEl,
  {
    placement: "bottom",
    middleware: [
      offset(10), // More space to clearly separate from input
      flip(), // Flip if no room below
      shift({ padding: 10 }) // Keep some padding from viewport edges
    ],
    whileElementsMounted: autoUpdate,
  }
);

// Computed properties
const displayValue = computed(() => {
  if (!props.modelValue) return "";
  return props.modelValue;
});

const formattedHours = computed(() => {
  return internalHours.value.toString().padStart(2, "0");
});

const formattedMinutes = computed(() => {
  return internalMinutes.value.toString().padStart(2, "0");
});

const currentTimeString = computed(() => {
  return `${formattedHours.value}:${formattedMinutes.value}`;
});

// Functions
const parseTimeValue = (timeString) => {
  if (!timeString) {
    internalHours.value = 0;
    internalMinutes.value = 0;
    return;
  }

  const [hours, minutes] = timeString.split(":").map(Number);
  internalHours.value = isNaN(hours) ? 0 : Math.max(0, Math.min(23, hours));

  // Round minutes to nearest 15-minute interval
  const roundedMinutes = Math.round((isNaN(minutes) ? 0 : minutes) / 15) * 15;
  internalMinutes.value = roundedMinutes >= 60 ? 0 : roundedMinutes;
};

// Watch for external model value changes - declared after parseTimeValue since
// {immediate: true} invokes this callback synchronously during setup, before any
// later const declaration in this file would be initialized
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue && newValue !== currentTimeString.value) {
      parseTimeValue(newValue);
    }
  },
  { immediate: true }
);

const adjustHours = (delta) => {
  let newHours = internalHours.value + delta;
  if (newHours > 23) newHours = 0;
  if (newHours < 0) newHours = 23;
  internalHours.value = newHours;
};

const adjustMinutes = (delta) => {
  let newMinutes = internalMinutes.value + delta;
  if (newMinutes >= 60) newMinutes = 0;
  if (newMinutes < 0) newMinutes = 45;
  internalMinutes.value = newMinutes;
};

const togglePopover = () => {
  if (props.disabled) return;
  
  if (!popoverOpen.value) {
    // Initialize with current value when opening
    if (props.modelValue) {
      parseTimeValue(props.modelValue);
    }
  }
  
  popoverOpen.value = !popoverOpen.value;
};

const closePopover = () => {
  popoverOpen.value = false;
};

const confirmSelection = () => {
  const newValue = currentTimeString.value;
  emit("update:modelValue", newValue);
  emit("change", newValue);
  closePopover();
};

const handleBlur = () => {
  emit("blur");
};

// Close popover when clicking outside
const handleClickOutside = (event) => {
  if (
    popoverOpen.value &&
    popoverEl.value &&
    inputRef.value &&
    !popoverEl.value.contains(event.target) &&
    !inputRef.value.contains(event.target)
  ) {
    closePopover();
  }
};

// Lifecycle hooks
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("input", {
      id: __props.inputId,
      ref_key: "inputRef",
      ref: inputRef,
      name: __props.name,
      value: displayValue.value,
      type: "text",
      class: _normalizeClass(["form-control", __props.inputClass]),
      disabled: __props.disabled,
      readonly: "",
      placeholder: __props.placeholder,
      onClick: togglePopover,
      onBlur: handleBlur
    }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_2),
    _createCommentVNode(" Time Picker Popover "),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (popoverOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "popoverEl",
            ref: popoverEl,
            style: _normalizeStyle(_unref(popoverStyles)),
            class: "time-picker-popover popover-content bg-white border shadow-sm rounded p-3"
          }, [
            _createElementVNode("div", _hoisted_3, [
              _createCommentVNode(" Hours Section "),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-outline-secondary btn-sm time-arrow",
                  disabled: __props.disabled,
                  onClick: _cache[0] || (_cache[0] = $event => (adjustHours(1)))
                }, [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("i", { class: "fas fa-chevron-up" }, null, -1 /* CACHED */)
                ]))], 8 /* PROPS */, _hoisted_5),
                _createElementVNode("div", _hoisted_6, _toDisplayString(formattedHours.value), 1 /* TEXT */),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-outline-secondary btn-sm time-arrow",
                  disabled: __props.disabled,
                  onClick: _cache[1] || (_cache[1] = $event => (adjustHours(-1)))
                }, [...(_cache[5] || (_cache[5] = [
                  _createElementVNode("i", { class: "fas fa-chevron-down" }, null, -1 /* CACHED */)
                ]))], 8 /* PROPS */, _hoisted_7)
              ]),
              _createCommentVNode(" Separator "),
              _cache[8] || (_cache[8] = _createElementVNode("div", { class: "time-separator" }, [
                _createElementVNode("strong", null, ":")
              ], -1 /* CACHED */)),
              _createCommentVNode(" Minutes Section "),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-outline-secondary btn-sm time-arrow",
                  disabled: __props.disabled,
                  onClick: _cache[2] || (_cache[2] = $event => (adjustMinutes(15)))
                }, [...(_cache[6] || (_cache[6] = [
                  _createElementVNode("i", { class: "fas fa-chevron-up" }, null, -1 /* CACHED */)
                ]))], 8 /* PROPS */, _hoisted_9),
                _createElementVNode("div", _hoisted_10, _toDisplayString(formattedMinutes.value), 1 /* TEXT */),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-outline-secondary btn-sm time-arrow",
                  disabled: __props.disabled,
                  onClick: _cache[3] || (_cache[3] = $event => (adjustMinutes(-15)))
                }, [...(_cache[7] || (_cache[7] = [
                  _createElementVNode("i", { class: "fas fa-chevron-down" }, null, -1 /* CACHED */)
                ]))], 8 /* PROPS */, _hoisted_11)
              ])
            ]),
            _createCommentVNode(" Action Buttons "),
            _createElementVNode("div", { class: "d-flex justify-content-end gap-2 mt-3" }, [
              _createElementVNode("button", {
                type: "button",
                class: "btn btn-secondary btn-sm",
                onClick: closePopover
              }, " Abbrechen "),
              _createElementVNode("button", {
                type: "button",
                class: "btn btn-primary btn-sm",
                onClick: confirmSelection
              }, " Übernehmen ")
            ])
          ], 4 /* STYLE */))
        : _createCommentVNode("v-if", true)
    ]))
  ]))
}
}

}